import { makeStyles } from '@material-ui/core';
import {
  boschBlue,
  boschGray,
} from '../../modules/theme/boschColors';

const useStyles = makeStyles(theme => ({
  card: {
    border: `1px solid ${boschGray[85]}`,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: '#000000',
  },
  header: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${boschGray[90]}`,
    backgroundColor: boschGray[95],
    fontSize: '0.7rem',
    fontWeight: 700,
    color: boschGray[40],
    textTransform: 'uppercase',
    letterSpacing: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  link: {
    fontSize: '0.7rem',
    color: boschBlue[50],
    textDecoration: 'none',
    fontWeight: 600,
    '&:hover': { textDecoration: 'underline' },
  },
  /*
   * The 16:9 aspect-ratio trick: wrap the iframe in a relatively
   * positioned container with `padding-bottom: 56.25%` (= 9 / 16),
   * then absolutely-fill the iframe inside. Works on every browser
   * we support without needing the CSS `aspect-ratio` property,
   * which is recent enough we don't want to require it for the
   * iframe-y "this just shows a video" use case.
   */
  ratio: {
    position: 'relative',
    width: '100%',
    paddingBottom: '56.25%',
    height: 0,
    backgroundColor: '#000000',
  },
  frame: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
  },
  error: {
    padding: theme.spacing(2),
    color: boschGray[40],
    fontStyle: 'italic',
    fontSize: '0.85rem',
    backgroundColor: '#FFFFFF',
  },
}));

export type YouTubeVideoProps = {
  /**
   * 11-character YouTube video ID. We accept the ID directly rather
   * than a full URL so admins can't accidentally paste a link that
   * resolves to a playlist, a channel, or an unrelated tracking
   * redirect. If you have a URL like `https://youtu.be/<id>`, take
   * just the 11-char path.
   */
  videoId: string;
  title?: string;
  /**
   * Start time in seconds. Useful when the relevant section of a long
   * keynote starts 12 minutes in — drops the viewer straight there.
   */
  startSeconds?: number;
};

/*
 * YouTube IDs are exactly 11 characters from `A-Z`, `a-z`, `0-9`, `_`
 * and `-`. We validate before building the embed URL so a typo or a
 * stray "watch?v=" prefix produces a clear "invalid" message instead
 * of an iframe that loads YouTube's "Video unavailable" splash.
 */
const VALID_ID = /^[A-Za-z0-9_-]{11}$/;

/**
 * Inline YouTube embed. Uses `youtube-nocookie.com` (YouTube's
 * privacy-enhanced mode) so visiting a content page that contains a
 * video doesn't drop tracking cookies on the user until they actually
 * click play.
 *
 * Why a dedicated widget instead of just leaning on `iframe-embed`:
 *   - `iframe-embed` requires the host to be on its allow-list AND
 *     forces the admin to know the exact embed URL format. This
 *     widget normalises both — admin pastes the ID, we build a safe
 *     URL with `?rel=0&modestbranding=1`.
 *   - The 16:9 wrapper is built in, so a video at 1/2 width still
 *     renders with correct proportions. With raw `iframe-embed` the
 *     admin would need to know the right height to set per width.
 */
export const YouTubeVideo = ({
  videoId,
  title,
  startSeconds,
}: YouTubeVideoProps) => {
  const classes = useStyles();

  if (!videoId || !VALID_ID.test(videoId.trim())) {
    return (
      <div className={classes.card}>
        <div className={classes.error}>
          Invalid YouTube video ID. Expected the 11-character ID from a
          URL like <code>https://youtu.be/<strong>dQw4w9WgXcQ</strong></code>
          — got <code>{videoId || '(empty)'}</code>.
        </div>
      </div>
    );
  }

  const id = videoId.trim();
  const start =
    typeof startSeconds === 'number' && startSeconds > 0
      ? `&start=${Math.floor(startSeconds)}`
      : '';
  /*
   * `rel=0`            → don't show unrelated videos at the end.
   * `modestbranding=1` → reduce the YouTube logo overlay.
   * `playsinline=1`    → on iOS Safari, plays inline instead of
   *                      auto-fullscreening.
   * We deliberately don't set `autoplay=1` — auto-playing video on
   * a portal page is a UX hostility we won't ship by default.
   */
  const src = `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&playsinline=1${start}`;
  const watchUrl = `https://www.youtube.com/watch?v=${id}`;

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <span>{title ?? 'YouTube'}</span>
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.link}
        >
          Open on YouTube ↗
        </a>
      </div>
      <div className={classes.ratio}>
        <iframe
          src={src}
          title={title ?? `YouTube video ${id}`}
          className={classes.frame}
          loading="lazy"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          /* Sandbox locked down: scripts + same-origin needed by the
           * YouTube player; popups so "Watch on YouTube" inside the
           * player works; presentation for fullscreen. No top-nav,
           * no forms — both unnecessary for video playback. */
          sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    </div>
  );
};
